<template>
  <Transition name="fade">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
    >
      <div class="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div class="flex items-start justify-between gap-4">
          <div class="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600">
            <img :src="errorIcon" alt="" class="h-6 w-6" />
          </div>
          <div class="flex-1 space-y-2 text-left">
            <p class="m-0 text-[1.5rem] font-semibold text-black">
              {{ $t('There was a problem') }}
            </p>
            <p class="m-0 text-[1.3rem] text-[color:var(--gray)] whitespace-pre-line">
              {{ errorMsg }}
            </p>
          </div>
          <button
            class="text-black/60 hover:text-black"
            aria-label="Close"
            @click="close"
          >
            <img :src="closeIcon" alt="" class="h-5 w-5" />
          </button>
        </div>
        <div class="mt-6 flex justify-end gap-3">
          <button
            class="rounded-xl border border-black/10 px-4 py-2 text-[1.3rem] text-black hover:border-black/30"
            @click="close"
          >
            {{ $t('Close') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useUIStore } from "@/stores/useUIStore";
import { GlobalErrors, NetworkError } from "@/enum/constants";
import ErrorAlertIcon from "@/assets/images/error-alert.svg";
import CloseIcon from "@/assets/images/close-small.svg";

const uiStore = useUIStore();
const router = useRouter();
const { t } = useI18n();

const errorMsg = ref("");
const openLogin = ref(false);
const errorIcon = ErrorAlertIcon;
const closeIcon = CloseIcon;

const isOpen = computed(() => uiStore.globalErrorIsOpen);
const errorData = computed(() => uiStore.globalErrorData);

watch(errorData, (newValue) => {
  if (uiStore.globalErrorOnlyPopup === true) {
    uiStore.setGlobalErrorData({});
    uiStore.openLogin();
    openLogin.value = false;
    return;
  }
  if (newValue && Object.keys(newValue).length > 0) {
    manageError(newValue);
  }
});

function close() {
  uiStore.setGlobalErrorData({});
  uiStore.setGlobalErrorCode(null);
  uiStore.closeGlobalError();
  if (uiStore.globalErrorRedirectUrl != null) {
    router.push(uiStore.globalErrorRedirectUrl);
    uiStore.setGlobalErrorRedirectUrl(null);
    return;
  }
  if (openLogin.value) {
    openLogin.value = false;
    uiStore.openLogin();
  }
}

function manageError(error: Record<string, unknown>) {
  const resp = error.response as Record<string, unknown> | undefined;
  const status = resp?.status as number | undefined;
  switch (status) {
    case -1:
      errorMsg.value = resp?.message as string ?? "";
      break;
    case GlobalErrors.ERROR_400: {
      const msgs = (resp?.data as Record<string, unknown>)?.messages as string[] | undefined;
      errorMsg.value = msgs?.[0] ?? t("sorry_we_are_having_problems_please_try_again_later");
      break;
    }
    case GlobalErrors.ERROR_500: {
      const data = resp?.data as Record<string, unknown> | string | undefined;
      const msgs = typeof data === "object" ? (data as Record<string, unknown>)?.messages as string[] | undefined : undefined;
      if (msgs?.[0]) {
        errorMsg.value = msgs[0];
      } else if (
        (error as Record<string, unknown>).code === NetworkError.ABORTED ||
        (error as Record<string, unknown>).message?.toString().includes(NetworkError.NET) ||
        (typeof data === "string" && data.includes(NetworkError.PROXY))
      ) {
        uiStore.setGlobalErrorRedirectUrl("/DisconnectError");
        close();
      }
      errorMsg.value = t("sorry_we_are_having_problems_please_try_again_later");
      break;
    }
    default:
      errorMsg.value = t("sorry_we_are_having_problems_please_try_again_later");
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
